import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Table, List, Typography, Empty, Button, Row } from 'antd';

import { CreateTeamContext } from './../../CreateTeam/index';
import { reducerActions } from './../../CreateTeam/reducer';
import { PlayerInfo } from 'components';
import { api } from 'api';
import { getErrorMessage } from 'helpers/feedbackMessageHelper';

import useAdmin from "hooks/useAdmin";

const { Text } = Typography;

const RecommendationsModal = ({ isVisible, handleCancel, teamId }) => {

    const { adminData } = useAdmin();

    const { state, dispatch } = useContext(CreateTeamContext);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [recommendations, setRecommendations] = useState([]);

    const loadRecommendations = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.team.getTransferRecommendations(teamId);
            setRecommendations(response.data ?? []);
        } catch (err) {
            const errorMessage = err.response ? err.response.data.error : 'generic';
            setError(getErrorMessage(errorMessage));
        }

        setLoading(false);
    };

    useEffect(() => {
        if (isVisible && teamId) {
            loadRecommendations();
        }
    }, [isVisible, teamId]);

    const applyRecommendations = () => {
        const playersOutIds = recommendations
            .map(recommendation => recommendation.playerOut?.id)
            .filter(Boolean);

        const remainingPlayers = state.selectedPlayers.filter(player => !playersOutIds.includes(player.id));

        const playersIn = recommendations
            .map(recommendation => recommendation.playerIn)
            .filter(playerIn => playerIn && !remainingPlayers.some(player => player.id === playerIn.id));

        const updatedPlayers = [
            ...remainingPlayers,
            ...playersIn
        ];

        dispatch({
            type: reducerActions.selectPlayer,
            payload: { selectedPlayers: updatedPlayers }
        });

        handleCancel();
    };

    const columns = [
        {
            title: 'Player In',
            dataIndex: 'playerIn',
            key: 'playerIn',
            render: (text, record) => (
                <>
                    {record.playerIn?.id &&
                        <List.Item key={record.playerIn.id}>
                            <PlayerInfo
                                key={record.playerIn.id}
                                player={record.playerIn}
                                showAvatar={adminData.sportIsCricket()}
                                />
                        </List.Item>
                    }
                </>
            )
        },
        {
            title: 'Player Out',
            dataIndex: 'playerOut',
            key: 'playerOut',
            render: (text, record) => (
                <>
                    {record.playerOut?.id &&
                        <List.Item key={record.playerOut.id}>
                            <PlayerInfo
                                key={record.playerOut.id}
                                player={record.playerOut}
                                showAvatar={adminData.sportIsCricket()}
                                />
                        </List.Item>
                    }
                </>
            )
        }
    ];

    return (
        <Modal
            title="AI Transfer Recommendations"
            open={isVisible}
            onCancel={handleCancel}
            footer={
                <Row type="flex" justify="end" align="middle">
                    <Button
                        type="primary"
                        disabled={loading || !!error || recommendations.length < 1}
                        onClick={applyRecommendations}
                        >
                        Apply Transfers
                    </Button>
                </Row>
            }
            >
            {error ? (
                <Empty description={error} />
            ) : (
                <>
                <Table
                    rowKey={record => record.id}
                    loading={loading}
                    dataSource={[...recommendations]}
                    columns={columns}
                    pagination={false}
                    expandable={{
                        showExpandColumn: false,
                        expandedRowKeys: recommendations.map(record => record.id),
                        rowExpandable: record => !!record.reason,
                        expandedRowRender: record => <Text type="secondary">{record.reason}</Text>
                    }}
                    locale={{ emptyText: <Empty description="No recommendations available right now." /> }}
                    />
                    
                </>
            )}
        </Modal>
    );
};

RecommendationsModal.defaultProps = {
    isVisible: false
}

RecommendationsModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    handleCancel: PropTypes.func.isRequired,
    teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default RecommendationsModal;
